import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ROUTES from '@/routes/routes'
import type { MyPrepStatus } from '../../redux/types/charterTypes'

interface PreTeamPrepCardProps {
  roomId: string
  inviteCode: string
  status: MyPrepStatus | null
}

/**
 * What a student sees before they are placed on a team: prep is homework they
 * can do now, and it goes with them to whichever team they join.
 */
export function PreTeamPrepCard({ roomId, inviteCode, status }: PreTeamPrepCardProps) {
  const navigate = useNavigate()
  const prep = status?.prep ?? null
  const prepOpen = status?.round_status === 'in_progress'
  const done = prep?.status === 'completed'
  const approved = done && prep?.profile_approved

  const openPrep = () =>
    navigate(`${ROUTES.STUDENT.CHARTER_PREP(roomId)}?inviteCode=${inviteCode}`)

  return (
    <div className='rounded-lg border border-indigo-100 bg-white p-5 text-left shadow-sm'>
      <p className='font-medium text-dark-blue'>
        You&apos;re in. Your instructor will place you on a team.
      </p>

      {!prepOpen ? (
        <p className='mt-2 text-sm text-gray-500'>
          Your individual prep opens once your instructor starts the charter session.
        </p>
      ) : (
        <>
          <p className='mt-2 text-sm text-gray-600'>
            You don&apos;t need a team to do your individual prep. Your answers are
            saved and go with you to whichever team you join.
          </p>

          <div className='mt-4 flex flex-wrap items-center justify-between gap-3'>
            <div className='flex items-center gap-2 text-sm text-gray-600'>
              {approved ? (
                <>
                  <CheckCircle2 className='h-4 w-4 text-emerald-500' />
                  <span>Prep complete and approved</span>
                </>
              ) : prep ? (
                <span>
                  {prep.completed_sections} of {prep.total_sections} sections done
                </span>
              ) : (
                <span>Not started yet</span>
              )}
            </div>
            <Button
              onClick={openPrep}
              className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
            >
              <Sparkles className='mr-1.5 h-4 w-4' />
              {approved ? 'Review my prep' : prep ? 'Continue my prep' : 'Start my prep'}
            </Button>
          </div>
        </>
      )}

      <p className='mt-4 text-xs text-gray-400'>
        This page updates on its own — you can leave it open.
      </p>
    </div>
  )
}
